import { AdditionalFilters, EntityRole, IntegrationTagFilter, NetworkColumn } from '../helpers/types';
import { Filter as GridFilter } from '../../../grid/gridFilters/types';
import { convertGridFilterToExtJsFilters } from '../../../grid/utils/filters';

export class AdditionalFiltersHelper {
  public static setAdditionalFilters(
    additionalFiltersFromGrid: GridFilter<NetworkColumn>[],
    additionalFilters: AdditionalFilters
  ) {
    for (const filter of additionalFiltersFromGrid) {
      const filterKey: NetworkColumn = Object.keys(filter)[0] as NetworkColumn;

      switch (filterKey) {
        case NetworkColumn.TYPE:
          AdditionalFiltersHelper.addTypeFilter(filter, additionalFilters);
          break;
        case NetworkColumn.ROLE:
        case NetworkColumn.MIGRATED_TO_ANOTHER_COMPANY:
        case NetworkColumn.IN_DELETED_FOLDER:
          AdditionalFiltersHelper.addArrayFilterInAdditionalFilters(filter, additionalFilters, filterKey);
          break;
        case NetworkColumn.POWER:
          AdditionalFiltersHelper.addPowerFilter(filter, additionalFilters);
          break;
        case NetworkColumn.AWS_INTEGRATION_TAGS:
          AdditionalFiltersHelper.addAwsIntegrationTagsFilter(filter, additionalFilters);
          break;
        case NetworkColumn.POLICY_TYPE:
          AdditionalFiltersHelper.addPolicyTypeFilter(filter, filterKey, additionalFilters);
          break;
        case NetworkColumn.AVAILABLE_POLICIES:
          AdditionalFiltersHelper.addPolicyTemplateFilter(filter, additionalFilters);
          break;
        case NetworkColumn.MANAGEMENT_STATUS:
        case NetworkColumn.SECURITY_ISSUES:
        case NetworkColumn.POLICY_STATUS:
        case NetworkColumn.PENDING_ACTIVITY:
          AdditionalFiltersHelper.transformAdditionalFilterToKeyAndBoolean(filter, filterKey, additionalFilters);
          break;
        default:
          console.error(`Unknown additional filter key: ${filterKey}. Skipping...`);
          break;
      }
    }

    return additionalFilters;
  }

  private static addTypeFilter(filter: GridFilter<NetworkColumn>, additionalFilters: AdditionalFilters) {
    if (!filter.type.in.length) {
      return;
    }

    additionalFilters.type = filter.type.in;
  }

  private static addArrayFilterInAdditionalFilters(
    filter: GridFilter<NetworkColumn>,
    additionalFilters: AdditionalFilters,
    filterKey: NetworkColumn
  ) {
    if (!filter[filterKey].in.length) {
      return;
    }

    if (!additionalFilters[filterKey]) {
      additionalFilters[filterKey] = [];
    }

    for (const value of filter[filterKey].in) {
      additionalFilters[filterKey].push(value);
    }
  }

  private static addPowerFilter(filter: GridFilter<NetworkColumn>, additionalFilters: AdditionalFilters) {
    const filteredValues = filter[NetworkColumn.POWER].in;
    if (!filteredValues || filteredValues.length === 0) {
      return;
    }

    if (filteredValues.includes(Constants.ProtectedEntities.AWSPowerState.POWER_STATE_RUNNING)) {
      additionalFilters['stateCodeRunning'] = Constants.ProtectedEntities.AWSPowerState.POWER_STATE_RUNNING;
    }

    if (filteredValues.includes(Constants.ProtectedEntities.AWSPowerState.POWER_STATE_STOPPED)) {
      additionalFilters['stateCodeStopped'] = Constants.ProtectedEntities.AWSPowerState.POWER_STATE_STOPPED;
    }

    if (filteredValues.includes(Constants.ProtectedEntities.AWSPowerState.POWER_STATE_TERMINATED)) {
      additionalFilters['stateCodeTerminated'] = Constants.ProtectedEntities.AWSPowerState.POWER_STATE_TERMINATED;
    }
  }

  private static addAwsIntegrationTagsFilter(filter: GridFilter<NetworkColumn>, additionalFilters: AdditionalFilters) {
    const filterValue = convertGridFilterToExtJsFilters(filter)[0]?.value as string;

    if (!filterValue || filterValue === '') {
      return;
    }

    // create both key and value query, these two queries will be pushed into an $or query in console php
    const keyQuery: IntegrationTagFilter = {
      type: Constants.ProtectedEntities.EPS.FILTERS.TAGS.AWS_EC2,
      attribute: filterValue,
      valueTag: '',
    };

    const valueQuery: IntegrationTagFilter = {
      type: Constants.ProtectedEntities.EPS.FILTERS.TAGS.AWS_EC2,
      attribute: '',
      valueTag: filterValue,
    };

    additionalFilters.tagStore = [keyQuery, valueQuery];
  }

  private static addPolicyTypeFilter(
    filter: GridFilter<NetworkColumn>,
    filterKey: NetworkColumn,
    additionalFilters: AdditionalFilters
  ) {
    if (!filter[filterKey].in.length) {
      return;
    }

    // do nothing when 'All' is selected
    if (filter[filterKey].in.length === 3) {
      return;
    }

    // if another value will be added to the filter, throw error to update this code
    if (filter[filterKey].in.length > 3) {
      throw Error('Policy type filter should not have more than 3 values.');
    }

    AdditionalFiltersHelper.transformAdditionalFilterToKeyAndBoolean(filter, filterKey, additionalFilters);
  }

  private static addPolicyTemplateFilter(filter: GridFilter<NetworkColumn>, additionalFilters: AdditionalFilters) {
    const filterValue = filter[NetworkColumn.AVAILABLE_POLICIES]?.equals;
    if (!filterValue) {
      return;
    }

    additionalFilters['policyTemplate'] = filterValue;
  }

  /**
   * Transforms additional filters to key and boolean
   *
   * example:
   *  [{ managementStatus: { in: ['managedNetworkV2', 'unmanagedNetworkV2'] } }]
   *  will be added to additional filters as
   *  { ..., managementStatus: { managedNetworkV2: 1, unmanagedNetworkV2: 1 }, ...}
   *
   * @param {Filter<NetworkColumn>} filter
   * @param {NetworkColumn} filterKey
   * @param {AdditionalFilters} additionalFilters
   * @private
   */
  private static transformAdditionalFilterToKeyAndBoolean(
    filter: GridFilter<NetworkColumn>,
    filterKey: NetworkColumn,
    additionalFilters: AdditionalFilters
  ) {
    if (!filter[filterKey].in.length) {
      return;
    }

    for (const filterValue of filter[filterKey].in as Array<string>) {
      additionalFilters[filterValue] = 1;
    }
  }
}
